import { Component, computed, effect, inject, signal } from '@angular/core';
import { filter } from 'rxjs';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import {
  AsideComponent,
  ColorScheme,
  Header,
  type HeaderSearchSuggestion,
  MainComponent,
  type MelloLogoMode,
  ThemeService,
} from '@crm/ui';
import {
  MESSENGER_AUTH,
  MessengerNotificationCenterComponent,
  MessengerNotificationFacade,
} from '@crm/messenger';
import { RoleService } from './core/role.service';
import { APP_ROUTE_CATALOG, isRouteAvailable } from './core/routing/route-catalog';
import { ContactOverlaysComponent } from './shared/contact/contact-overlays';
import { MessengerRuntime } from './messenger/messenger-runtime';
import {
  resolveCatalogEnvironment,
  resolveMessengerRuntimeEnvironment,
} from './core/capability/capability.resolver';
import { CommandCenterState } from './pages/dashboard/command-center.state';
import { ROLE_LABELS } from './core/experience-context';

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'mello.sidebar.collapsed.v1';

const PRIMARY_ROUTES = ['/dashboard', '/appointments', '/insights'] as const;

function primaryRank(route: string): number {
  const index = PRIMARY_ROUTES.indexOf(route as (typeof PRIMARY_ROUTES)[number]);
  return index === -1 ? PRIMARY_ROUTES.length : index;
}

const MOCK_ROLE_OPTIONS = [
  { value: 'loan_officer', label: 'Loan Officer' },
  { value: 'clr', label: 'CL Representative' },
  { value: 'cxs', label: 'CX Specialist' },
  { value: 'manager', label: 'Manager' },
  { value: 'administrator', label: 'Administrator' },
];

@Component({
  imports: [
    RouterOutlet,
    Header,
    AsideComponent,
    MainComponent,
    MessengerNotificationCenterComponent,
    ContactOverlaysComponent,
  ],
  selector: 'app[ui]',
  templateUrl: './app.html',
  styleUrl: './app.css',
  host: {
    '[attr.data-sidebar-collapsed]': 'collapsed || null',
  },
})
export class App {
  protected readonly headerAriaLabel = 'Global';
  protected readonly asideAriaLabel = 'Sidebar';
  protected readonly mainAriaLabel = 'Page content';
  protected readonly skipLinkLabel = 'Skip to content';
  protected readonly searchPlaceholder =
    'Type anything you’re searching for';
  private readonly themeService = inject(ThemeService);
  private readonly router = inject(Router);
  private readonly compactViewport = signal(
    typeof matchMedia === 'function' && matchMedia('(width <= 760px)').matches,
  );
  protected readonly brandMode = computed<MelloLogoMode>(() =>
    this.themeService.colorScheme() === ColorScheme.Dark ? 'light' : 'dark',
  );
  protected brandCompact(): boolean {
    return this.collapsed || this.compactViewport();
  }
  protected readonly avatarSrc = computed(() =>
    this.themeService.colorScheme() === ColorScheme.Dark
      ? '/assets/account-avatar-dark.svg'
      : '/assets/account-avatar.svg',
  );

  private readonly messengerAuth = inject(MESSENGER_AUTH, { optional: true });
  private readonly notificationFacade = inject(MessengerNotificationFacade, {
    optional: true,
  });
  protected readonly notificationUnread = computed(
    () => this.notificationFacade?.unreadCount() ?? 0,
  );
  protected readonly currentRole = computed(
    () => this.messengerAuth?.roles()[0] ?? '',
  );
  protected readonly userRoleLabel = computed(
    () => ROLE_LABELS[this.roleService.currentRole()] ?? this.currentRole(),
  );
  protected readonly userName = computed(
    () => this.messengerAuth?.account()?.name ?? this.roleService.currentUser().name,
  );
  protected readonly userEmail = computed(
    () => this.messengerAuth?.account()?.email ?? this.roleService.currentUser().email,
  );
  protected readonly userInitials = computed(() => initials(this.userName()));
  private readonly roleService = inject(RoleService);
  private readonly runtime = inject(MessengerRuntime, { optional: true });
  private readonly commandCenterState = inject(CommandCenterState);
  protected readonly navItems = computed(() => {
    const badges = this.commandCenterState.navBadges();
    const combinedBadge = badges['/leads'] || badges['/dashboard'];
    return this.roleService
      .navigation()
      .filter((item) => item.route !== '/leads')
      .map((item) => ({
        ...item,
        badge: item.route === '/dashboard' ? combinedBadge : badges[item.route],
      }));
  });
  protected readonly activeRole = this.roleService.currentRole;
  protected readonly roleOptions = computed(() => {
    const env = this.runtime
      ? resolveMessengerRuntimeEnvironment(this.runtime.config)
      : 'production';
    return env === 'mock' ? MOCK_ROLE_OPTIONS : [];
  });
  changeRole(role: string): void {
    const env = this.runtime
      ? resolveMessengerRuntimeEnvironment(this.runtime.config)
      : 'production';
    if (env !== 'mock') return;
    this.roleService.switchRole(role);
  }

  private readonly runtimeEnvironment = computed(() =>
    this.runtime ? resolveCatalogEnvironment(this.runtime.config) : 'production',
  );
  protected readonly searchSuggestions = computed<readonly HeaderSearchSuggestion[]>(() => {
    const environment = this.runtimeEnvironment();
    this.activeRole();
    return APP_ROUTE_CATALOG.filter(
      (entry) =>
        entry.label &&
        entry.description &&
        !entry.redirectTo &&
        !entry.fullPath.includes(':') &&
        !entry.fullPath.includes('*') &&
        entry.section !== 'system' &&
        isRouteAvailable(entry, environment) &&
        this.roleService.canAccessRoute(entry.fullPath),
    )
      .sort((a, b) => primaryRank(a.fullPath) - primaryRank(b.fullPath) || a.order - b.order)
      .map((entry) => ({
      label: entry.label,
      description: entry.description ?? '',
      route: entry.fullPath,
    }));
  });

  protected onSuggestionSelected(suggestion: HeaderSearchSuggestion): void {
    void this.router.navigateByUrl(suggestion.route);
  }

  protected onSearchSubmitted(query: string): void {
    if (!query) return;
    void this.router.navigate(['/search'], { queryParams: { q: query } });
  }

  constructor() {
    effect(() => {
      const env = this.runtime
        ? resolveMessengerRuntimeEnvironment(this.runtime.config)
        : 'mock';
      if (env === 'mock' || this.messengerAuth?.account()) {
        if (!this.router.url.startsWith('/leads')) {
          this.commandCenterState.ensureLeadsLoaded();
        }
      }
    });
    if (typeof matchMedia === 'function') {
      matchMedia('(width <= 760px)').addEventListener('change', (e) =>
        this.compactViewport.set(e.matches),
      );
    }

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        document.getElementById('main-content')?.focus({ preventScroll: true });
      });
  }
  protected sidebarOpen = false;
  protected collapsed = readSidebarCollapsed();

  toggleSidebar(): void {
    this.sidebarOpen = !this.sidebarOpen;
  }

  toggleCollapsed(): void {
    this.collapsed = !this.collapsed;
    writeSidebarCollapsed(this.collapsed);
  }

  signOut(): void {
    void this.messengerAuth?.signOut();
  }
}

function readSidebarCollapsed(): boolean {
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeSidebarCollapsed(collapsed: boolean): void {
  try {
    window.localStorage.setItem(
      SIDEBAR_COLLAPSED_STORAGE_KEY,
      String(collapsed),
    );
  } catch {
    return;
  }
}

function initials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join('')
      .toUpperCase()
  );
}
